<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">交易记录</h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">查看所有成交订单、支付状态、佣金结算与退款信息。</p>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-12">
      <div class="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
    </div>

    <div v-else class="rounded-lg bg-white shadow dark:bg-gray-800">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/60">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">订单ID</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">用户</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">套餐</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">价格方案</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">成交金额</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">结算价</th>
              <th class="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">佣金</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">支付状态</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">佣金状态</th>
              <th class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">支付时间</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="record in records" :key="record.subscriptionId" class="hover:bg-gray-50 dark:hover:bg-gray-900/40">
              <td class="px-4 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{{ record.subscriptionId }}</td>
              <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ record.username }}</td>
              <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ record.planName }}</td>
              <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ record.pricingName || '--' }}</td>
              <td class="px-4 py-4 text-right text-sm font-medium text-gray-900 dark:text-gray-100">{{ formatCurrency(record.orderAmount) }}</td>
              <td class="px-4 py-4 text-right text-sm text-gray-500 dark:text-gray-400">{{ record.settlementAmount != null ? formatCurrency(record.settlementAmount) : '--' }}</td>
              <td class="px-4 py-4 text-right text-sm font-medium text-emerald-600 dark:text-emerald-400">{{ record.commissionAmount != null ? formatCurrency(record.commissionAmount) : '--' }}</td>
              <td class="px-4 py-4 text-sm">
                <span :class="getPaymentStatusClass(record.paymentStatus)">{{ getPaymentStatusLabel(record.paymentStatus) }}</span>
              </td>
              <td class="px-4 py-4 text-sm">
                <span v-if="record.commissionStatus" :class="getCommissionStatusClass(record.commissionStatus)">{{ getCommissionStatusLabel(record.commissionStatus) }}</span>
                <span v-else class="inline-flex rounded-full bg-gray-100 px-2 text-xs font-semibold leading-5 text-gray-600 dark:bg-gray-700 dark:text-gray-300">无佣金</span>
              </td>
              <td class="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">{{ formatDateTime(record.paymentTime || record.createdAt) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="records.length === 0" class="py-12 text-center">
        <p class="text-sm text-gray-500 dark:text-gray-400">暂无订单记录</p>
      </div>

      <div v-if="pagination.total > 0" class="border-t border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/60">
        <div class="flex items-center justify-between">
          <div class="text-sm text-gray-700 dark:text-gray-300">
            共 {{ pagination.total }} 条记录
          </div>
          <div class="flex gap-2">
            <button
              @click="prevPage"
              :disabled="pagination.page === 1"
              class="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              上一页
            </button>
            <button
              @click="nextPage"
              :disabled="pagination.page * pagination.limit >= pagination.total"
              class="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';
import DemoNotice from '@/components/DemoNotice.vue';
import { getTenantOrdersAPI } from '@/api/tenantConsole';
import { useAuthStore } from '@/stores/auth';
import toast from '@/utils/toaster';
import type { TenantOrderRecord } from '@/types/tenantConsole';

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);

const records = ref<TenantOrderRecord[]>([]);
const loading = ref(false);
const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0,
});

/**
 * 格式化金额。
 */
const formatCurrency = (cents: number): string => {
  return `¥${(cents / 100).toFixed(2)}`;
};

/**
 * 格式化日期时间。
 */
const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * 获取支付状态文案。
 */
const getPaymentStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    PAID: '已支付',
    REFUNDED: '已退款',
  };
  return labels[status] || status;
};

/**
 * 获取支付状态样式。
 */
const getPaymentStatusClass = (status: string): string => {
  const classes: Record<string, string> = {
    PAID: 'inline-flex rounded-full bg-emerald-100 px-2 text-xs font-semibold leading-5 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
    REFUNDED: 'inline-flex rounded-full bg-rose-100 px-2 text-xs font-semibold leading-5 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300',
  };
  return classes[status] || '';
};

/**
 * 获取佣金状态文案。
 */
const getCommissionStatusLabel = (status: string): string => {
  const labels: Record<string, string> = {
    PENDING: '待结算',
    SETTLED: '已结算',
    REFUNDED: '已退款',
  };
  return labels[status] || status;
};

/**
 * 获取佣金状态样式。
 */
const getCommissionStatusClass = (status: string): string => {
  const classes: Record<string, string> = {
    PENDING: 'inline-flex rounded-full bg-yellow-100 px-2 text-xs font-semibold leading-5 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
    SETTLED: 'inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    REFUNDED: 'inline-flex rounded-full bg-red-100 px-2 text-xs font-semibold leading-5 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  };
  return classes[status] || '';
};

/**
 * 加载订单记录。
 */
const loadRecords = async () => {
  loading.value = true;
  try {
    const res = await getTenantOrdersAPI({
      page: pagination.page,
      limit: pagination.limit,
    });
    if (res.status === 200 && res.data) {
      records.value = res.data.records;
      pagination.total = res.data.total;
    }
  } catch (error) {
    toast.error('加载订单记录失败');
    console.error(error);
  } finally {
    loading.value = false;
  }
};

/**
 * 上一页。
 */
const prevPage = () => {
  if (pagination.page > 1) {
    pagination.page--;
    loadRecords();
  }
};

/**
 * 下一页。
 */
const nextPage = () => {
  if (pagination.page * pagination.limit < pagination.total) {
    pagination.page++;
    loadRecords();
  }
};

onMounted(() => {
  loadRecords();
});
</script>
